import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaymentSheetEventsEnum, Stripe } from '@capacitor-community/stripe';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  loadStripe,
  StripeElements,
  Stripe as StripeJS,
  StripePaymentElement,
} from '@stripe/stripe-js';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface PaymentIntent {
  clientSecret: string;
  amount: number;
  currency: string;
  metadata: {
    packageId: string;
    packageType: 'words' | 'images';
    userId: string;
  };
}

export interface PaymentResult {
  success: boolean;
  paymentIntentId?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class StripePaymentService {
  private stripe: StripeJS | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  private isInitialized = false;
  private initializationSubject = new BehaviorSubject<boolean>(false);

  // Store pending payment info for mobile Browser close detection
  private pendingPaymentInfo: {
    sessionId: string;
    packageId: string;
    packageType: 'words' | 'images';
  } | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Initialize Stripe with publishable key from secure API
   */
  async initializeStripe(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('💳 Initializing Stripe...');

      // Fetch Stripe configuration from secure API endpoint
      const configResponse = await this.http
        .get<{
          success: boolean;
          publishableKey: string;
        }>(`${environment.apiUrl}/api/stripe/config`)
        .toPromise();

      if (!configResponse?.success || !configResponse.publishableKey) {
        throw new Error('Failed to fetch Stripe configuration from server');
      }

      const publishableKey = configResponse.publishableKey;

      // Initialize Capacitor Stripe plugin on mobile
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Initializing Capacitor Stripe plugin for mobile');
        try {
          await Stripe.initialize({
            publishableKey,
            // stripeAccount removed - not needed for single account
          });
          console.log('✅ Capacitor Stripe plugin initialized');
        } catch (error) {
          console.error('❌ Failed to initialize Capacitor Stripe:', error);
          throw error;
        }
      }

      // Initialize web Stripe.js for web platform
      if (!Capacitor.isNativePlatform()) {
        console.log('🌐 Initializing Stripe.js for web');
        const initPromise = loadStripe(publishableKey);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Stripe initialization timeout')),
            10000
          )
        );

        this.stripe = (await Promise.race([
          initPromise,
          timeoutPromise,
        ])) as StripeJS;

        if (!this.stripe) {
          throw new Error('Failed to load Stripe');
        }
        console.log('✅ Stripe.js initialized for web');
      }

      this.isInitialized = true;
      this.initializationSubject.next(true);
      console.log('✅ Stripe initialized successfully with secure key');
    } catch (error) {
      console.error('❌ Failed to initialize Stripe:', error);
      this.isInitialized = false;
      this.initializationSubject.next(false);
      throw error;
    }
  }

  /**
   * Get initialization status
   */
  getInitializationStatus(): Observable<boolean> {
    return this.initializationSubject.asObservable();
  }

  /**
   * Get authentication headers
   */
  private async getAuthHeaders(): Promise<any> {
    const token = await this.authService.getAuthToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get current user ID
   */
  private getCurrentUserId(): string {
    const user = this.authService.getCurrentUser();
    return user?.email || 'anonymous';
  }

  /**
   * Create payment intent on backend
   */
  createPaymentIntent(
    packageId: string,
    packageType: 'words' | 'images',
    amount: number,
    currency: string = 'usd'
  ): Observable<PaymentIntent> {
    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<PaymentIntent>(
          `${environment.apiUrl}/api/stripe/create-payment-intent`,
          {
            packageId,
            packageType,
            amount: Math.round(amount * 100), // Convert to cents
            currency,
            metadata: {
              packageId,
              packageType,
              userId: this.getCurrentUserId(),
            },
          },
          { headers }
        );
      })
    );
  }

  /**
   * Check if we're on a mobile device
   */
  private isMobileDevice(): boolean {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768
    );
  }

  /**
   * Setup payment element with mobile optimization
   */
  async setupPaymentElement(clientSecret: string): Promise<void> {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    try {
      const isMobile = this.isMobileDevice();
      console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');

      // Create elements instance with mobile-optimized settings
      this.elements = this.stripe.elements({
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#3b82f6',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            spacingUnit: isMobile ? '8px' : '4px',
            borderRadius: isMobile ? '12px' : '8px',
          },
        },
      });

      // Create payment element with only card payments and no billing collection
      this.paymentElement = this.elements.create('payment', {
        layout: isMobile ? 'accordion' : 'tabs',
        // Only enable card payments - remove all other methods
        paymentMethodOrder: ['card'],
        // Disable all billing detail collection to avoid input issues
        fields: {
          billingDetails: 'never',
        },
        // Explicitly disable all wallet payments
        wallets: {
          applePay: 'never',
          googlePay: 'never',
        },
      });

      console.log('✅ Payment element created with mobile optimization');
    } catch (error) {
      console.error('❌ Failed to setup payment element:', error);
      throw error;
    }
  }

  /**
   * Mount payment element to DOM
   */
  async mountPaymentElement(containerId: string): Promise<void> {
    if (!this.paymentElement) {
      throw new Error('Payment element not created');
    }

    try {
      // Check if container exists
      const container = document.getElementById(containerId);
      if (!container) {
        throw new Error(`Container element #${containerId} not found`);
      }

      console.log('✅ Container element found:', container);
      console.log('✅ Container dimensions:', {
        width: container.offsetWidth,
        height: container.offsetHeight,
        visible: container.offsetParent !== null,
      });

      await this.paymentElement.mount(`#${containerId}`);
      console.log('✅ Payment element mounted');

      // Add event listeners to track Stripe element events
      this.paymentElement.on('ready', () => {
        console.log('✅ Stripe payment element ready');
      });

      this.paymentElement.on('change', (event) => {
        console.log('✅ Stripe payment element changed:', event);
      });

      this.paymentElement.on('focus', (event) => {
        console.log('✅ Stripe payment element focused:', event);
      });

      // Remove loading text
      const loadingText = container.querySelector('.text-center.text-gray-500');
      if (loadingText) {
        loadingText.remove();
      }

      // Wait a moment and check if element is visible
      setTimeout(() => {
        // Look for Stripe-specific elements
        const stripeInputs = container.querySelectorAll(
          'input[data-elements-stable-field-name]'
        );
        const stripeIframe = container.querySelector(
          'iframe[src*="js.stripe.com"]'
        );
        const stripeElement =
          container.querySelector('[data-testid="payment-element"]') ||
          container.querySelector('.p-Input') ||
          container.querySelector('.p-CardNumberField') ||
          container.querySelector('.p-CardExpiryField') ||
          container.querySelector('.p-CardCvcField');

        console.log('✅ Stripe element visibility check:', {
          containerVisible: container.offsetParent !== null,
          hasStripeInputs: stripeInputs.length > 0,
          hasStripeIframe: !!stripeIframe,
          hasStripeElement: !!stripeElement,
          containerHeight: container.offsetHeight,
          stripeInputCount: stripeInputs.length,
        });

        // If no Stripe elements found, try to debug
        if (stripeInputs.length === 0 && !stripeIframe) {
          console.warn(
            '⚠️ No Stripe input elements found. Container HTML:',
            container.innerHTML.substring(0, 500)
          );
        }
      }, 1000);
    } catch (error) {
      console.error('❌ Failed to mount payment element:', error);
      throw error;
    }
  }

  /**
   * Confirm payment
   */
  async confirmPayment(): Promise<PaymentResult> {
    if (!this.stripe || !this.paymentElement) {
      throw new Error('Stripe or payment element not initialized');
    }

    try {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements!,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        console.error('❌ Payment failed:', error);
        return {
          success: false,
          error: error.message,
        };
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        return {
          success: true,
          paymentIntentId: paymentIntent.id,
        };
      }

      return {
        success: false,
        error: 'Payment was not completed',
      };
    } catch (error) {
      console.error('❌ Payment confirmation error:', error);
      return {
        success: false,
        error: 'An unexpected error occurred',
      };
    }
  }

  /**
   * Create a Stripe Checkout Session (hosted checkout page)
   */
  createCheckoutSession(
    packageId: string,
    packageType: 'words' | 'images',
    amount: number,
    currency: string = 'usd',
    packageItems?: Array<{ packageId: string; packageType: 'words' | 'images' }>
  ): Observable<any> {
    console.log('🔍 createCheckoutSession called with:', {
      packageId,
      packageType,
      amount,
      currency,
      packageItems,
    });

    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      console.error('❌ User not authenticated in createCheckoutSession');
      throw new Error('User not authenticated');
    }

    console.log('✅ User authenticated:', currentUserId);

    // Build success and cancel URLs
    // For mobile, use production URL with hash - app will detect via deep link listener
    const isMobile = Capacitor.isNativePlatform();
    let successUrl: string;
    let cancelUrl: string;

    if (isMobile) {
      // For mobile, use webapp with deep link - Stripe redirects to web page,
      // which immediately redirects to app via deep link
      // Use dev URL if in dev mode, otherwise use prod URL
      const baseUrl = !environment.production
        ? 'https://translation-app-dev-648c3.web.app'
        : 'https://translation-5c1b8.web.app';
      successUrl = `${baseUrl}/payment-success-redirect?paymentSuccess=true&packageId=${encodeURIComponent(
        packageId
      )}&packageType=${encodeURIComponent(
        packageType
      )}&sessionId={CHECKOUT_SESSION_ID}`;
      cancelUrl = `${baseUrl}/payment-cancel`;
    } else {
      // For web, use current origin (localhost for dev, production for prod)
      const baseUrl = window.location.origin;
      successUrl = `${baseUrl}/payment-success?paymentSuccess=true&packageId=${encodeURIComponent(
        packageId
      )}&packageType=${encodeURIComponent(
        packageType
      )}&sessionId={CHECKOUT_SESSION_ID}`;
      cancelUrl = `${baseUrl}/payment-cancel`;
    }

    console.log('🔍 URL generation:', {
      isMobile,
      successUrl,
      cancelUrl,
      windowOrigin: window.location.origin,
      platform: isMobile ? 'mobile (deep link)' : 'web',
    });

    const requestBody = {
      packageId,
      packageType,
      packageItems,
      amount,
      currency,
      successUrl,
      cancelUrl,
    };

    console.log('💳 Creating checkout session:', requestBody);
    console.log(
      '🔍 API URL:',
      `${environment.apiUrl}/api/stripe/create-checkout-session`
    );

    return from(this.getAuthHeaders()).pipe(
      switchMap((headers) => {
        console.log('🔍 Auth headers obtained:', !!headers.Authorization);
        console.log('🔍 Making HTTP request to create checkout session');

        return this.http.post<any>(
          `${environment.apiUrl}/api/stripe/create-checkout-session`,
          requestBody,
          { headers }
        );
      })
    );
  }

  /**
   * Redirect to Stripe Checkout (hosted page)
   * Uses Capacitor Browser plugin for mobile, window.location.href for web
   */
  async redirectToCheckout(
    checkoutUrl: string,
    sessionId?: string,
    packageId?: string,
    packageType?: 'words' | 'images'
  ): Promise<void> {
    console.log('🚀 Redirecting to Stripe Checkout:', checkoutUrl);

    try {
      if (Capacitor.isNativePlatform()) {
        console.log('📱 Native platform detected - using Capacitor Browser');

        // Store payment info for Browser close detection
        if (sessionId && packageId && packageType) {
          this.pendingPaymentInfo = {
            sessionId,
            packageId,
            packageType,
          };
          console.log(
            '💾 Stored pending payment info:',
            this.pendingPaymentInfo
          );
        }

        await Browser.open({ url: checkoutUrl });
        console.log('✅ Browser opened successfully');
      } else {
        console.log('🌐 Web platform detected - using window.location.href');
        window.location.href = checkoutUrl;
        console.log('✅ Window redirect initiated');
      }
    } catch (error) {
      console.error('❌ Failed to redirect to checkout:', error);
      throw error;
    }
  }

  /**
   * Present native Stripe PaymentSheet for mobile
   */
  async presentPaymentSheet(
    packageId: string,
    packageType: 'words' | 'images',
    amount: number
  ): Promise<PaymentResult> {
    console.log('🚀 STARTING presentPaymentSheet method');
    console.log('📱 Presenting PaymentSheet:', {
      packageId,
      packageType,
      amount,
      isNative: Capacitor.isNativePlatform(),
      isInitialized: this.isInitialized,
    });

    if (!this.isInitialized) {
      console.error('❌ Stripe not initialized!');
      return {
        success: false,
        error: 'Stripe not initialized',
      };
    }

    try {
      // Call backend to create PaymentIntent
      console.log('💳 Calling backend to create PaymentSheet...');
      const authHeaders = await this.getAuthHeaders();
      console.log('🔑 Auth headers:', {
        hasAuth: !!authHeaders,
        keys: Object.keys(authHeaders),
      });

      const response = await this.http
        .post<any>(
          `${environment.apiUrl}/api/stripe/payment-sheet`,
          {
            packageId,
            packageType,
            amount,
            currency: 'usd',
          },
          { headers: authHeaders }
        )
        .toPromise();

      console.log('💳 Backend API call completed');

      console.log('🔍 Backend response:', {
        success: response?.success,
        hasPaymentIntent: !!response?.paymentIntent,
        hasEphemeralKey: !!response?.ephemeralKey,
        hasCustomer: !!response?.customer,
      });

      if (
        !response?.success ||
        !response.paymentIntent ||
        !response.ephemeralKey ||
        !response.customer
      ) {
        throw new Error(
          `Backend response incomplete: ${JSON.stringify(response)}`
        );
      }

      console.log('✅ PaymentIntent, Customer, and Ephemeral Key received');
      console.log('🔑 PaymentIntent length:', response.paymentIntent?.length);
      console.log('🔑 EphemeralKey length:', response.ephemeralKey?.length);
      console.log('👤 Customer ID:', response.customer);

      // Create PaymentSheet with the PaymentIntent
      console.log('🔧 Creating PaymentSheet...');
      try {
        await Stripe.createPaymentSheet({
          paymentIntentClientSecret: response.paymentIntent,
          customerEphemeralKeySecret: response.ephemeralKey,
          customerId: response.customer,
          merchantDisplayName: 'TilHub Translation',
        });
        console.log('✅ PaymentSheet created successfully');
      } catch (createError: any) {
        console.error('❌ PaymentSheet creation failed:', createError);
        throw new Error(
          `PaymentSheet creation failed: ${
            createError.message || JSON.stringify(createError)
          }`
        );
      }

      // Present the PaymentSheet
      console.log('📱 Presenting PaymentSheet...');
      let presentResult;
      try {
        presentResult = await Stripe.presentPaymentSheet();
        console.log('📱 PaymentSheet presented, result:', presentResult);
        console.log('📱 PaymentSheet result type:', typeof presentResult);
        console.log('📱 PaymentSheet result keys:', Object.keys(presentResult || {}));
        console.log('📱 PaymentSheet paymentResult:', presentResult?.paymentResult);
        console.log('📱 PaymentSheetEventsEnum.Completed:', PaymentSheetEventsEnum.Completed);
        console.log('📱 Comparison result:', presentResult?.paymentResult === PaymentSheetEventsEnum.Completed);
      } catch (presentError: any) {
        console.error('❌ PaymentSheet presentation failed:', presentError);
        throw new Error(
          `PaymentSheet presentation failed: ${
            presentError.message || JSON.stringify(presentError)
          }`
        );
      }

      // Check if paymentResult matches Completed - handle both string comparison and enum
      const paymentResult = presentResult?.paymentResult as string | undefined;
      const isCompleted = 
        paymentResult === PaymentSheetEventsEnum.Completed ||
        paymentResult === 'paymentSheetCompleted';
      
      console.log('🔍 Payment completion check:', {
        paymentResult: presentResult?.paymentResult,
        enumValue: PaymentSheetEventsEnum.Completed,
        isCompleted: isCompleted,
        rawComparison: presentResult?.paymentResult === PaymentSheetEventsEnum.Completed,
        stringComparison: presentResult?.paymentResult === 'paymentSheetCompleted'
      });

      if (isCompleted) {
        console.log('✅ Payment completed via PaymentSheet');

        // Extract paymentIntentId from client secret
        let paymentIntentId: string | undefined;
        try {
          paymentIntentId = this.extractPaymentIntentId(
            response.paymentIntent
          );
          console.log('✅ PaymentIntent ID extracted:', paymentIntentId);
        } catch (extractError: any) {
          console.error('❌ Failed to extract PaymentIntent ID:', extractError);
          // Still return success, but without paymentIntentId
          console.warn('⚠️ Continuing without paymentIntentId for manual confirmation');
        }

        return {
          success: true,
          paymentIntentId,
        };
      } else {
        console.log(
          '⚠️ PaymentSheet cancelled or failed:',
          presentResult?.paymentResult
        );
        return {
          success: false,
          error: `Payment ${presentResult?.paymentResult || 'unknown'}`,
        };
      }
    } catch (error: any) {
      console.error('❌ PaymentSheet error:', error);
      return {
        success: false,
        error: error.message || 'Payment failed',
      };
    }
  }

  /**
   * Extract PaymentIntent ID from client secret
   * Client secret format: pi_xxxxx_secret_yyyyy
   */
  private extractPaymentIntentId(clientSecret: string): string {
    if (!clientSecret) {
      throw new Error('Client secret is empty');
    }
    
    console.log('🔍 Extracting PaymentIntent ID from:', clientSecret.substring(0, 50) + '...');
    
    // Try splitting by _secret_
    const parts = clientSecret.split('_secret_');
    if (parts.length > 0 && parts[0]) {
      const paymentIntentId = parts[0];
      console.log('✅ Extracted PaymentIntent ID:', paymentIntentId);
      return paymentIntentId;
    }
    
    // Fallback: if client secret already looks like a PaymentIntent ID (starts with pi_)
    if (clientSecret.startsWith('pi_')) {
      // Check if it contains _secret_ - if not, it might already be just the ID
      if (!clientSecret.includes('_secret_')) {
        console.log('✅ Client secret appears to be PaymentIntent ID already:', clientSecret);
        return clientSecret;
      }
    }
    
    throw new Error(`Unable to extract PaymentIntent ID from client secret: ${clientSecret.substring(0, 50)}...`);
  }

  /**
   * Confirm PaymentIntent on backend
   */
  private async confirmPaymentIntent(paymentIntentId: string): Promise<any> {
    console.log('💳 Confirming PaymentIntent:', paymentIntentId);

    const authHeaders = await this.getAuthHeaders();
    const response = await this.http
      .post<any>(
        `${environment.apiUrl}/api/stripe/confirm-payment`,
        { paymentIntentId },
        { headers: authHeaders }
      )
      .toPromise();

    if (!response?.success) {
      throw new Error('Failed to confirm payment');
    }

    console.log('✅ Payment confirmed on backend:', response);
    return response;
  }

  /**
   * Get pending payment info (for mobile Browser close detection)
   */
  getPendingPaymentInfo(): {
    sessionId: string;
    packageId: string;
    packageType: 'words' | 'images';
  } | null {
    return this.pendingPaymentInfo;
  }

  /**
   * Clear pending payment info after processing
   */
  clearPendingPaymentInfo(): void {
    console.log('🧹 Clearing pending payment info');
    this.pendingPaymentInfo = null;
  }

  /**
   * Handle payment success callback - simplified version without verification
   */
  async handlePaymentSuccessCallback(
    packageId: string,
    packageType: 'words' | 'images'
  ): Promise<void> {
    console.log('💳 handlePaymentSuccessCallback() called:', {
      packageId,
      packageType,
    });

    // No need to verify payment - Stripe webhook already processed it
    // Just log the success and let the calling component handle UI updates
    console.log(
      '✅ Payment success callback handled - webhook already processed payment'
    );

    return;
  }

  /**
   * Clean up payment elements
   */
  cleanup(): void {
    if (this.paymentElement) {
      this.paymentElement.unmount();
      this.paymentElement = null;
    }
    if (this.elements) {
      this.elements = null;
    }
  }

  /**
   * Format amount for display
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}
